// Shared primitives for the Financials views — matches the approved mockup
// (mockups/financials-v1.html) + styles.js tokens. The v2 primitives (PanelHd,
// Kv, Attn, PanelNote, linkBtn) follow mockups/financials-v2.html.
import React from "react";
import { C, font, mono } from "../../../lib/styles.js";

export const panel = {
  background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
  padding: 18, fontFamily: font,
};

export const lblStyle = {
  fontSize: 11, fontWeight: 400, textTransform: "uppercase",
  letterSpacing: "0.12em", color: C.muted, fontFamily: font,
};

export const money = (n, cents = true) => {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0,
  });
};

export const th = {
  textAlign: "left", fontSize: 10.5, textTransform: "uppercase",
  letterSpacing: "0.1em", color: C.muted, fontWeight: 400,
  padding: "8px 10px", borderBottom: `1px solid ${C.border}`,
};
export const td = { padding: "9px 10px", borderBottom: `1px solid ${C.border}`, fontSize: 12.5 };
export const tdNum = { ...td, textAlign: "right", fontFamily: mono, fontSize: 12 };

export const Badge = ({ tone, children }) => {
  const tones = {
    over: { background: "#FDECEC", color: C.danger },
    soon: { background: "#FDF3E1", color: "#B45309" },
    ok: { background: "#E7F6EF", color: "#047857" },
    sandbox: { background: "#EDE9FE", color: "#6D28D9" },
    prod: { background: "#E7F6EF", color: "#047857" },
  };
  return (
    <span style={{
      fontSize: 10.5, padding: "2px 8px", borderRadius: 10, fontWeight: 400,
      fontFamily: font, ...(tones[tone] || tones.ok),
    }}>{children}</span>
  );
};

export const Loading = ({ label = "Loading…" }) => (
  <div style={{ color: C.muted, fontSize: 13, fontFamily: font, padding: 28, textAlign: "center" }}>{label}</div>
);

export const ErrorPanel = ({ error, onRetry }) => (
  <div style={{ ...panel, textAlign: "center", color: C.muted, fontSize: 13 }}>
    <div style={{ marginBottom: 10 }}>{error?.message || "Something went wrong"}</div>
    {onRetry && (
      <button style={{
        font: "inherit", fontSize: 12, padding: "6px 13px", borderRadius: 6,
        border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer",
      }} onClick={onRetry}>Retry</button>
    )}
  </div>
);

export const RefreshButton = ({ onClick }) => (
  <button title="Refresh from QuickBooks" onClick={onClick} style={{
    font: "inherit", fontSize: 12, padding: "5px 11px", borderRadius: 6,
    border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer",
    color: C.muted,
  }}>↻ Refresh</button>
);

/** Segmented control (30/60/90, report presets). */
export const Seg = ({ options, value, onChange }) => (
  <span style={{
    display: "inline-flex", border: `1px solid ${C.border}`, borderRadius: 6,
    overflow: "hidden",
  }}>
    {options.map((o) => (
      <button key={o.value ?? o} onClick={() => onChange(o.value ?? o)} style={{
        font: "inherit", fontSize: 12, padding: "6px 14px", border: "none",
        cursor: "pointer",
        background: (o.value ?? o) === value ? C.text : C.surface,
        color: (o.value ?? o) === value ? "#fff" : C.text,
      }}>{o.label ?? o}</button>
    ))}
  </span>
);

// ── v2 primitives (mockups/financials-v2.html; first used by the bank data
// screens, slice 1). They sit inside the v1 container with its padding off,
// `{ ...panel, padding: 0 }`, and carry their own spacing, as the mockup's
// .panel does. Secondary text is C.muted, attention C.capture, money in C.green.

const TONE = { in: C.green, attn: C.capture, dim: C.muted };

/** Inline text action with a bottom border (the mockup's link in .attn and .hd), as a button. */
export const linkBtn = {
  font: "inherit", fontSize: 12, fontWeight: 400, color: C.text, background: "none",
  border: "none", borderBottom: `1px solid ${C.border}`, borderRadius: 0,
  padding: 0, cursor: "pointer", whiteSpace: "nowrap", fontFamily: font,
};

/** A panel's header row: title, a note beside it, and an optional right-hand element. */
export const PanelHd = ({ title, note, right }) => (
  <div style={{
    display: "flex", alignItems: "baseline", gap: 10, padding: "11px 14px",
    borderBottom: `1px solid ${C.border}`, minWidth: 0,
  }}>
    <span style={{ fontSize: 13, fontWeight: 400, color: C.text, whiteSpace: "nowrap" }}>{title}</span>
    {note ? (
      <span style={{
        fontSize: 11.5, color: C.muted, minWidth: 0, overflow: "hidden",
        textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{note}</span>
    ) : null}
    {right ? <span style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>{right}</span> : null}
  </div>
);

/**
 * Key/value grid. rows: [{ k, small?, v, tone?: "in" | "attn" | "dim" }].
 * Default: the value right-aligned in DM Mono with tabular figures (money,
 * counts). `text`: a fixed key column and prose values, left-aligned (the
 * mockup's Settings cards).
 */
export const Kv = ({ rows, text = false }) => (
  <div style={{
    display: "grid", gridTemplateColumns: text ? "130px minmax(0, 1fr)" : "minmax(0, 1fr) auto",
    gap: "6px 14px", fontSize: 13, padding: "10px 14px 12px", fontFamily: font,
  }}>
    {(rows ?? []).map((r, i) => (
      <React.Fragment key={i}>
        <span style={{ color: C.muted, minWidth: 0 }}>
          {r.k}
          {r.small ? <small style={{ display: "block", fontSize: 11, color: C.muted }}>{r.small}</small> : null}
        </span>
        <span style={text ? {
          minWidth: 0, fontSize: 13, color: TONE[r.tone] ?? C.text, overflowWrap: "anywhere",
        } : {
          fontFamily: mono, fontSize: 12.5, fontVariantNumeric: "tabular-nums", textAlign: "right",
          whiteSpace: "nowrap", color: TONE[r.tone] ?? C.text,
        }}>{r.v}</span>
      </React.Fragment>
    ))}
  </div>
);

/**
 * Attention list. items: [{ text (node), quiet?: boolean, action?: { label, onClick } }].
 * An amber dot per row, a grey one when quiet; the action sits at the right.
 */
export const Attn = ({ items, style }) => (
  <ul style={{
    listStyle: "none", margin: 0, padding: 0, background: C.surface,
    border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: font, ...style,
  }}>
    {(items ?? []).map((it, i) => (
      <li key={i} style={{
        display: "flex", gap: 10, alignItems: "center", padding: "9px 14px", fontSize: 13,
        minWidth: 0, borderBottom: i < items.length - 1 ? `1px solid ${C.border}` : "none",
      }}>
        <span aria-hidden="true" style={{
          width: 7, height: 7, borderRadius: "50%", display: "inline-block", flex: "none",
          background: it.quiet ? C.muted : C.capture,
        }} />
        <span style={{ minWidth: 0 }}>{it.text}</span>
        {it.action ? (
          <button type="button" style={{ ...linkBtn, marginLeft: "auto" }} onClick={it.action.onClick}>
            {it.action.label}
          </button>
        ) : null}
      </li>
    ))}
  </ul>
);

/** A v2 panel's footnote row. */
export const PanelNote = ({ children }) => (
  <div style={{
    padding: "8px 14px 10px", fontSize: 11.5, color: C.muted, lineHeight: 1.5,
    borderTop: `1px solid ${C.border}`, fontFamily: font,
  }}>{children}</div>
);

export const NotConnected = ({ onGoSettings }) => (
  <div style={{ ...panel, maxWidth: 420, margin: "50px auto", textAlign: "center" }}>
    <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>QuickBooks isn&apos;t connected</div>
    <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 14 }}>
      Connect the company file in Settings to see live numbers here.
    </div>
    <button style={{
      font: "inherit", fontSize: 12.5, padding: "8px 16px", borderRadius: 6,
      border: `1px solid ${C.text}`, background: C.text, color: "#fff", cursor: "pointer",
    }} onClick={onGoSettings}>Open Settings</button>
  </div>
);

// Date helpers (client-side presets) — LOCAL calendar dates, never UTC:
// toISOString() flips to tomorrow every evening after ~8pm New York time,
// which would jump MTD reports a day (or a month, on the 31st) early
// (codex review). en-CA locale formats as YYYY-MM-DD.
export const iso = (d) => d.toLocaleDateString("en-CA");
export const today = () => iso(new Date());
export const monthStart = () => { const n = new Date(); return iso(new Date(n.getFullYear(), n.getMonth(), 1)); };
export const presetRange = (preset) => {
  const n = new Date();
  const y = n.getFullYear(); const m = n.getMonth();
  switch (preset) {
    case "last_month": return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) };
    case "this_quarter": return { start: iso(new Date(y, Math.floor(m / 3) * 3, 1)), end: today() };
    case "ytd": return { start: iso(new Date(y, 0, 1)), end: today() };
    case "this_month":
    default: return { start: monthStart(), end: today() };
  }
};
/** Whole local-calendar-days from today until a YYYY-MM-DD date. */
export const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const n = new Date();
  const startOfToday = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  return Math.round((target - startOfToday) / 86_400_000);
};
